import { IconPaperclip } from "@tabler/icons-react";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@beweegbaas/ui/lib/utils";

const stackedListVariants = cva(
  // Same ring as Card, so stacked lists and cards read as one family.
  "overflow-hidden bg-background ring-1 ring-foreground/10 shadow-sm sm:rounded-lg dark:bg-gray-800/50 dark:shadow-none",
);

export type StackedListVariants = VariantProps<typeof stackedListVariants>;

export interface StackedListProps extends React.HTMLAttributes<HTMLDivElement> {}

function StackedList({ className, children, ...props }: StackedListProps) {
  return (
    <div className={cn(stackedListVariants(), className)} {...props}>
      {children}
    </div>
  );
}

function StackedListHeader({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("px-4 py-6 sm:px-6", className)} {...props}>
      {children}
    </div>
  );
}

function StackedListTitle({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3 className={cn("text-foreground text-base/7 font-semibold", className)} {...props}>
      {children}
    </h3>
  );
}

function StackedListDescription({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p className={cn("text-muted-foreground mt-1 max-w-2xl text-sm", className)} {...props}>
      {children}
    </p>
  );
}

function StackedListContent({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("", className)} {...props}>
      <dl className="divide-border divide-y">{children}</dl>
    </div>
  );
}

function StackedListItem({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6", className)} {...props}>
      {children}
    </div>
  );
}

function StackedListTerm({ className, children, ...props }: React.HTMLAttributes<HTMLElement>) {
  return (
    <dt className={cn("text-foreground text-sm font-semibold", className)} {...props}>
      {children}
    </dt>
  );
}

function StackedListDescriptionText({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLElement>) {
  return (
    <dd
      className={cn("text-foreground mt-1 text-sm/6 sm:col-span-2 sm:mt-0", className)}
      {...props}
    >
      {children}
    </dd>
  );
}

export interface AttachmentListProps extends React.HTMLAttributes<HTMLUListElement> {}

function AttachmentList({ className, children, ...props }: AttachmentListProps) {
  return (
    <ul
      role="list"
      className={cn("divide-border divide-y rounded-md border", className)}
      {...props}
    >
      {children}
    </ul>
  );
}

export interface AttachmentItemProps extends React.LiHTMLAttributes<HTMLLIElement> {
  name: string;
  size?: string;
  downloadUrl?: string;
  downloadLabel?: string;
  icon?: React.ReactNode;
}

function AttachmentItem({
  className,
  name,
  size,
  downloadUrl = "#",
  downloadLabel = "Download",
  icon,
  ...props
}: AttachmentItemProps) {
  return (
    <li
      className={cn("flex items-center justify-between py-4 pr-5 pl-4 text-sm/6", className)}
      {...props}
    >
      <div className="flex w-0 flex-1 items-center">
        {icon || (
          <IconPaperclip aria-hidden="true" className="text-muted-foreground size-5 shrink-0" />
        )}
        <div className="ml-4 flex min-w-0 flex-1 gap-2">
          <span className="text-foreground truncate font-medium">{name}</span>
          {size && <span className="text-muted-foreground shrink-0">{size}</span>}
        </div>
      </div>
      <div className="ml-4 shrink-0">
        <a
          href={downloadUrl}
          className="font-medium text-indigo-600 hover:text-indigo-500
            dark:text-indigo-400 dark:hover:text-indigo-300"
        >
          {downloadLabel}
        </a>
      </div>
    </li>
  );
}

// Attach sub-components
StackedList.Header = StackedListHeader;
StackedList.Title = StackedListTitle;
StackedList.Description = StackedListDescription;
StackedList.Content = StackedListContent;
StackedList.Item = StackedListItem;
StackedList.Term = StackedListTerm;
StackedList.DescriptionText = StackedListDescriptionText;
StackedList.AttachmentList = AttachmentList;
StackedList.AttachmentItem = AttachmentItem;

export { StackedList };
